/**
 * Typed client for the We-Co Admin Onboarding flows.
 *
 * Contract: services/people on feat/admin-onboarding (see the backend
 * contract note). Three transports, all existing shell helpers:
 *  - Public, unauthenticated invitation routes → plain fetch to
 *    `peopleInvitationUrl(...)` (dev: `/api/people/*` passthrough).
 *  - Authenticated People *core* routes (MFA self-service) →
 *    `fetchWithAuth(peopleApiUrl(...))`, like TwoFactorSettings; `/users/me/`
 *    reuses the shell's `fetchMe`/`patchMe`.
 *  - Admin routes → `SuiteApiClient(API_ADMIN_PREFIX)` like adminApi /
 *    orgAdminApi. Paths MUST start with "/" — SuiteApiClient concatenates
 *    `baseUrl + path` with no separator (CLAUDE.md, "What was actually broken" #1).
 */
import {
  API_ADMIN_PREFIX,
  fetchWithAuth,
  peopleApiUrl,
  peopleAuthUrl,
  peopleInvitationUrl,
  SuiteApiClient,
  SuiteApiError,
} from "@weco/suite-client";
import { fetchMe, patchMe } from "../../../api/preferences.api";
import type { UserMe } from "../../../api/types/preferences";
import type {
  AcceptInvitationBody,
  AcceptInvitationResponse,
  AdminInvitation,
  AppAccessRow,
  BrandingUploadContentType,
  BrandingUploadField,
  BrandingUploadUrl,
  BulkInviteResponse,
  BulkInviteRow,
  Department,
  DepartmentCreateBody,
  InvitationErrorBody,
  InvitationValidation,
  LoginResponse,
  MfaEmailEnrollment,
  MfaEnrollment,
  MfaRecoveryCodesResponse,
  MfaStatus,
  OnboardingSummary,
  OrgBranding,
  OrgEmailDnsStatus,
  OrgInviteBody,
  OrgOnboarding,
  OrgOnboardingPatch,
  OrgRole,
  OrgRoleDetail,
  OrgSecurityPolicy,
  Paginated,
  ReportInvitationResponse,
  RequestNewInvitationResponse,
  SendActivationBody,
} from "./types";

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

/** Error from a non-SuiteApiClient call: HTTP status + the parsed `{code, detail, …}` body. */
export class ObApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: (InvitationErrorBody & Record<string, unknown>) | null,
  ) {
    super(message);
    this.name = "ObApiError";
  }
  get code(): string | undefined {
    return this.body?.code;
  }
}

function bodyOf(err: unknown): Record<string, unknown> | null {
  if (err instanceof ObApiError) return err.body;
  if (err instanceof SuiteApiError && err.details && typeof err.details === "object") {
    return err.details as Record<string, unknown>;
  }
  return null;
}

/** HTTP status of any onboarding API error (0 for a network failure). */
export function obErrorStatus(err: unknown): number {
  if (err instanceof ObApiError) return err.status;
  if (err instanceof SuiteApiError) return err.code;
  return 0;
}

/** The backend's machine-readable `code` (e.g. "expired", "quota_exceeded"), if any. */
export function obErrorCode(err: unknown): string | undefined {
  const code = bodyOf(err)?.code;
  return typeof code === "string" ? code : undefined;
}

/** DRF field errors (`{field: [messages]}`) of a 400, flattened to strings. */
export function obFieldErrors(err: unknown): Record<string, string[]> {
  const body = bodyOf(err);
  const out: Record<string, string[]> = {};
  if (!body) return out;
  // drf-standardized-errors shape (serializer validation, permission errors):
  // `{type, errors: [{attr, code, detail}]}` — key by `attr`.
  if (Array.isArray(body.errors) && typeof body.type === "string") {
    for (const e of body.errors as Array<{ attr?: string | null; detail?: unknown }>) {
      if (typeof e?.detail !== "string") continue;
      const key = e.attr || "non_field_errors";
      (out[key] ??= []).push(e.detail);
    }
    return out;
  }
  for (const [k, v] of Object.entries(body)) {
    if (k === "code" || k === "detail") continue;
    if (Array.isArray(v)) out[k] = v.map(String);
    else if (typeof v === "string" && !["kind", "inviter_name", "personal_email_masked", "organization_name"].includes(k)) out[k] = [v];
  }
  return out;
}

/** Best human message for an error: `detail`, else the first field error, else `fallback`. */
export function obErrorMessage(err: unknown, fallback: string): string {
  const body = bodyOf(err);
  if (body && typeof body.detail === "string" && body.detail) return body.detail;
  const fields = Object.values(obFieldErrors(err));
  if (fields.length && fields[0].length) return fields[0][0];
  if (err instanceof SuiteApiError && err.message && !/^HTTP \d+/.test(err.message)) return err.message;
  return fallback;
}

async function parseJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

async function publicCall<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new ObApiError(
      (typeof data?.detail === "string" && data.detail) || `HTTP ${res.status}`,
      res.status,
      data as ObApiError["body"],
    );
  }
  return data as T;
}

async function coreCall<T>(path: string, body?: unknown, method?: string): Promise<T> {
  const res = await fetchWithAuth(peopleApiUrl(path), {
    method: method ?? (body === undefined ? "GET" : "POST"),
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new ObApiError(
      (typeof data?.detail === "string" && data.detail) || `HTTP ${res.status}`,
      res.status,
      data as ObApiError["body"],
    );
  }
  return data as T;
}

/* ------------------------------------------------------------------ */
/* §1 Public invitation endpoints (no session)                         */
/* ------------------------------------------------------------------ */

/** `GET /invitations/validate/?token=` — throws ObApiError with `code` (+ inviter/masked email for expired/revoked/accepted). */
export function validateInvitation(token: string): Promise<InvitationValidation> {
  return publicCall(`${peopleInvitationUrl("validate")}?token=${encodeURIComponent(token)}`);
}

/** `POST /invitations/accept/` — 400 `invalid_password` carries `password: [...]` (see `obFieldErrors`). */
export function acceptInvitation(body: AcceptInvitationBody): Promise<AcceptInvitationResponse> {
  return publicCall(peopleInvitationUrl("accept"), { method: "POST", body: JSON.stringify(body) });
}

/** `POST /invitations/request-new/` — activation: resent to the same personal email; member: inviter notified. */
export function requestNewInvitation(token: string): Promise<RequestNewInvitationResponse> {
  return publicCall(peopleInvitationUrl("request-new"), { method: "POST", body: JSON.stringify({ token }) });
}

/** `POST /invitations/report/` — revokes a pending invitation ("Didn't expect this? Report it"). Idempotent. */
export function reportInvitation(token: string): Promise<ReportInvitationResponse> {
  return publicCall(peopleInvitationUrl("report"), { method: "POST", body: JSON.stringify({ token }) });
}

/* ------------------------------------------------------------------ */
/* Login + MFA self-service + /users/me/                               */
/* ------------------------------------------------------------------ */

/**
 * `POST /auth/login/` (same call AcceptInvitationPage/LoginScreen make).
 * Does NOT store the session — pass `{user, access, refresh}` to the shell
 * store's `setUser`. A freshly accepted account has no MFA yet, so this
 * normally returns tokens.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(peopleAuthUrl("login/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new ObApiError(
      (typeof data?.detail === "string" && data.detail) || `HTTP ${res.status}`,
      res.status,
      data as ObApiError["body"],
    );
  }
  return data as unknown as LoginResponse;
}

/** True when a login response carries tokens (not an MFA / password-change step). */
export function isTokenLogin(r: LoginResponse): r is Extract<LoginResponse, { access: string }> {
  return typeof (r as { access?: unknown }).access === "string";
}

/** `GET /mfa/`. */
export function getMfaStatus(): Promise<MfaStatus> {
  return coreCall("mfa");
}

/** `POST /mfa/totp/enroll/` → `{secret, otpauth_uri, qr_svg}`. `password` is only needed to re-enrol while 2FA is on. */
export function enrollTotp(password?: string): Promise<MfaEnrollment> {
  return coreCall("mfa/totp/enroll", password ? { password } : {});
}

/** `POST /mfa/totp/confirm/ {code}` → activates 2FA and returns the recovery codes (once). */
export function confirmTotp(code: string): Promise<MfaRecoveryCodesResponse> {
  return coreCall("mfa/totp/confirm", { code: code.trim() });
}

/**
 * `POST /mfa/email/enroll/` → mails a code to the secondary email.
 * `email` is only needed when none is on file; `password` when 2FA is
 * already on or an address on file is being changed. 429 carries
 * `retry_after` (seconds); 400 `code` is `secondary_email_required` or
 * `secondary_email_is_login`; 503 means the mail couldn't be sent.
 */
export function enrollEmail(opts: { email?: string; password?: string } = {}): Promise<MfaEmailEnrollment> {
  const body: Record<string, string> = {};
  if (opts.email) body.email = opts.email.trim();
  if (opts.password) body.password = opts.password;
  return coreCall("mfa/email/enroll", body);
}

/** `POST /mfa/email/confirm/ {code}` → activates email 2FA and returns the recovery codes (once). */
export function confirmEmail(code: string): Promise<MfaRecoveryCodesResponse> {
  return coreCall("mfa/email/confirm", { code: code.trim() });
}

/** `GET /users/me/` (shell helper). */
export function getMe(): Promise<UserMe> {
  return fetchMe();
}

/** Clear `needs_onboarding_tour` so the spotlight ProductTour does not also fire after EmpTour. */
export function finishOnboardingTour(): Promise<UserMe> {
  return patchMe({ needs_onboarding_tour: false });
}

/* ------------------------------------------------------------------ */
/* Admin (SuiteApiClient, leading-slash paths)                         */
/* ------------------------------------------------------------------ */

const admin = new SuiteApiClient(API_ADMIN_PREFIX);

const org = (orgId: string) => `/organizations/${encodeURIComponent(orgId)}`;

function withQuery(path: string, params?: Record<string, string | number | undefined>): string {
  if (!params) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) sp.set(k, String(v));
  const q = sp.toString();
  return q ? `${path}?${q}` : path;
}

/** Accept either a DRF page or a bare array (some org-scoped lists return arrays). */
export function listOf<T>(data: Paginated<T> | T[] | null | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : (data.results ?? []);
}

/** People app_id → the `<module>:access` scope that gates it per role (null: no per-role scope exists). */
export const APP_MODULE_SCOPE: Readonly<Record<string, string | null>> = {
  email: "email:access",
  chat: "chat:access",
  drive: "drive:access",
  docs: "docs:access",
  calendar: "calendar:access",
  video: "video:access",
  homework: "office:access",
};

/** Does the Member role currently carry this app's access scope ("Everyone")? null if the app has no scope. */
export function memberHasModuleAccess(member: Pick<OrgRoleDetail, "scopes_detail">, appId: string): boolean | null {
  const scope = APP_MODULE_SCOPE[appId];
  if (!scope) return null;
  return member.scopes_detail.some((s) => s.scope === scope);
}

export const onboardingApi = {
  /* --- §3.1 onboarding state --------------------------------------- */
  get: (orgId: string) => admin.get<OrgOnboarding>(`${org(orgId)}/onboarding/`),
  /** `step_state` is merged server-side — send only the steps that changed. */
  update: (orgId: string, patch: OrgOnboardingPatch) => admin.patch<OrgOnboarding>(`${org(orgId)}/onboarding/`, patch),

  /* --- §3.2 one-call summary (Workspace ready + Setup guide) -------- */
  summary: (orgId: string) => admin.get<OnboardingSummary>(`${org(orgId)}/onboarding/summary/`),

  /* --- §2.2 super admin: (re)send the owner's activation ------------ */
  sendActivation: (orgId: string, body: SendActivationBody) =>
    admin.post<AdminInvitation>(`${org(orgId)}/send-activation/`, body),

  /* --- Step 2: domain / DNS ----------------------------------------- */
  emailDns: {
    get: (orgId: string) => admin.get<OrgEmailDnsStatus>(`${org(orgId)}/email-dns/`),
    verify: (orgId: string) => admin.post<OrgEmailDnsStatus>(`${org(orgId)}/email-dns/verify/`, {}),
    /** Owner: create the WeCo-hosted records for one of the org's own domains. */
    setup: (orgId: string, domain: string) =>
      admin.post<OrgEmailDnsStatus>(`${org(orgId)}/email-dns/setup/`, { domain }),
  },

  /* --- Step 3: modules & access ------------------------------------- */
  appAccess: {
    list: (orgId: string) => admin.get<AppAccessRow[]>(`${org(orgId)}/app-access/`),
    /** 409 when switching on a module outside the org's package. */
    set: (orgId: string, appId: string, enabled: boolean) =>
      admin.put<Pick<AppAccessRow, "id" | "app_id" | "app_name" | "enabled">>(`${org(orgId)}/app-access/`, {
        app_id: appId,
        enabled,
      }),
  },
  roles: {
    list: (orgId: string) => admin.get<Paginated<OrgRole> | OrgRole[]>(`${org(orgId)}/roles/`),
    get: (orgId: string, roleId: string) => admin.get<OrgRoleDetail>(`${org(orgId)}/roles/${encodeURIComponent(roleId)}/`),
    /** `scopes` are AdminPermission ids. */
    setScopes: (orgId: string, roleId: string, scopeIds: string[]) =>
      admin.patch<OrgRoleDetail>(`${org(orgId)}/roles/${encodeURIComponent(roleId)}/`, { scopes: scopeIds }),
  },

  /** The org's protected default Member role, with scopes. */
  async memberRole(orgId: string): Promise<OrgRoleDetail> {
    const roles = listOf(await onboardingApi.roles.list(orgId));
    const member = roles.find((r) => r.is_default) ?? roles.find((r) => r.is_protected && r.name === "Member");
    if (!member) throw new ObApiError("This organization has no default Member role.", 404, { code: "no_member_role" });
    return onboardingApi.roles.get(orgId, member.id);
  },

  /**
   * Step 3 "Everyone" vs "Admins only": add or remove the module's
   * `<module>:access` scope on the default Member role. Admin/Owner keep it
   * either way. The scope's id is looked up on the org's other roles (Owner
   * always holds every module scope) when Member does not hold it yet.
   * Returns the updated Member role; a no-op returns it unchanged.
   */
  async setModuleForEveryone(orgId: string, appId: string, everyone: boolean): Promise<OrgRoleDetail> {
    const scope = APP_MODULE_SCOPE[appId];
    if (!scope) throw new ObApiError(`No per-role access scope exists for "${appId}".`, 400, { code: "no_module_scope" });
    const member = await onboardingApi.memberRole(orgId);
    const has = member.scopes_detail.find((s) => s.scope === scope);
    if (everyone === !!has) return member;
    if (!everyone) {
      return onboardingApi.roles.setScopes(orgId, member.id, member.scopes.filter((id) => id !== has!.id));
    }
    let scopeId: string | undefined;
    for (const r of listOf(await onboardingApi.roles.list(orgId))) {
      if (r.id === member.id) continue;
      const detail = await onboardingApi.roles.get(orgId, r.id);
      scopeId = detail.scopes_detail.find((s) => s.scope === scope)?.id;
      if (scopeId) break;
    }
    if (!scopeId) throw new ObApiError(`The "${scope}" permission is not available in this organization.`, 400, { code: "scope_unavailable" });
    return onboardingApi.roles.setScopes(orgId, member.id, [...member.scopes, scopeId]);
  },

  /* --- Step 4: invitations ------------------------------------------ */
  invitations: {
    list: (orgId: string, params?: Record<string, string | number | undefined>) =>
      admin.get<Paginated<AdminInvitation> | AdminInvitation[]>(withQuery(`${org(orgId)}/invitations/`, params)),
    create: (orgId: string, body: OrgInviteBody) => admin.post<AdminInvitation>(`${org(orgId)}/invitations/`, body),
    /** Partial success: invalid rows are reported per row; 400 `quota_exceeded` creates nothing. */
    bulk: (orgId: string, rows: BulkInviteRow[]) =>
      admin.post<BulkInviteResponse>(`${org(orgId)}/invitations/bulk/`, { rows }),
    resend: (orgId: string, invitationId: string) =>
      admin.post<AdminInvitation>(`${org(orgId)}/invitations/${encodeURIComponent(invitationId)}/resend/`, {}),
    revoke: (orgId: string, invitationId: string) =>
      admin.delete<{ status: string }>(`${org(orgId)}/invitations/${encodeURIComponent(invitationId)}/`),
  },

  /* --- Step 5: security baseline ------------------------------------ */
  securityPolicy: {
    get: (orgId: string) => admin.get<OrgSecurityPolicy>(`${org(orgId)}/security-policy/`),
    update: (orgId: string, data: Partial<Pick<OrgSecurityPolicy, "require_mfa" | "mfa_grace_period_days">>) =>
      admin.patch<OrgSecurityPolicy>(`${org(orgId)}/security-policy/`, data),
  },

  /* --- Setup guide: departments + branding -------------------------- */
  departments: {
    list: (orgId: string) => admin.get<Paginated<Department> | Department[]>(`${org(orgId)}/departments/`),
    create: (orgId: string, body: DepartmentCreateBody) => admin.post<Department>(`${org(orgId)}/departments/`, body),
  },
  branding: {
    get: (orgId: string) => admin.get<OrgBranding>(`${org(orgId)}/branding/`),
    update: (orgId: string, data: Partial<OrgBranding>) => admin.put<OrgBranding>(`${org(orgId)}/branding/`, data),
    uploadUrl: (orgId: string, field: BrandingUploadField, contentType: BrandingUploadContentType, filename: string) =>
      admin.post<BrandingUploadUrl>(`${org(orgId)}/branding/upload-url/`, {
        field,
        content_type: contentType,
        filename,
      }),
  },

  /**
   * Upload a logo end to end: presigned URL → PUT the bytes → save the
   * object URL on the branding row. Returns the saved branding.
   */
  async uploadBrandingImage(orgId: string, field: BrandingUploadField, file: File): Promise<OrgBranding> {
    const contentType = (file.type || "image/png") as BrandingUploadContentType;
    const { upload_url, object_url } = await onboardingApi.branding.uploadUrl(orgId, field, contentType, file.name);
    const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
    if (!put.ok) throw new ObApiError(`Upload failed (HTTP ${put.status}).`, put.status, { code: "upload_failed" });
    return onboardingApi.branding.update(orgId, { [field]: object_url });
  },
};

export type OnboardingApi = typeof onboardingApi;
